import type { BreakdownStep } from '../../types/journey'
import { SCHEDULE_MODE_OPTIONS, WEEKDAY_CHIPS } from '../../utils/habitForm'
import { LEVEL_OPTIONS } from '../../utils/skillOptions'
import { WHEN_OPTIONS } from '../../utils/taskForm'

/**
 * The per-kind fields of a journey step in the review (Skills and Goals UX
 * passes, 2026-09-06): a habit step's schedule, a task step's by-when — and
 * the two mappings between the review's shape and the API's (a habit becomes
 * an RRULE; the engine's suggested habit_plan becomes the chooser's state)
 * live next door in stepKinds.tsx so this file exports components only.
 */
const chip = (on: boolean) => `min-h-touch rounded-full border px-3 py-1.5 text-xs font-medium transition-colors lg:min-h-0 ${on ? 'border-brand bg-brand text-on-brand' : 'border-strong bg-surface text-secondary hover:border-brand'}`

export type Update = (field: keyof BreakdownStep, value: unknown) => void

/** A habit step's schedule, in the review: how often and at what time. */
export function HabitStepFields({ step, update }: { step: BreakdownStep; update: Update }) {
  const h = step.habit || {}
  const set = (patch: Partial<NonNullable<BreakdownStep['habit']>>) => update('habit', { ...h, ...patch })
  const mode = h.schedule_mode || ''
  const days = h.schedule_days || []
  return (
    <div className="space-y-2" data-testid="journey-habit-fields">
      <div className="text-xs text-muted">A habit: when does it come round?</div>
      <div className="flex flex-wrap gap-1.5" role="group" aria-label="How often">
        {SCHEDULE_MODE_OPTIONS.map((o) => (
          <button key={o.value} type="button" aria-pressed={mode === o.value} onClick={() => set({ schedule_mode: mode === o.value ? '' : o.value })} className={chip(mode === o.value)} data-testid={`journey-habit-${o.value}`}>{o.label}</button>
        ))}
      </div>
      {mode === 'days' && (
        <div className="flex flex-wrap gap-1.5" role="group" aria-label="Which days">
          {WEEKDAY_CHIPS.map((d) => {
            const on = days.includes(d.value)
            return <button key={d.value} type="button" aria-pressed={on} onClick={() => set({ schedule_days: on ? days.filter((x) => x !== d.value) : [...days, d.value] })} className={chip(on)}>{d.label}</button>
          })}
        </div>
      )}
      <label className="flex items-center gap-2 text-xs text-secondary">
        <span>At</span>
        <input type="time" step={60} value={h.schedule_time || ''} onChange={(e) => set({ schedule_time: e.target.value })} aria-label="Time of day"
               className="min-h-touch rounded-md border border-soft bg-surface px-2 text-sm text-primary lg:min-h-0 lg:py-1" data-testid="journey-habit-time" />
        <span className="text-muted">The minutes above are how long each time takes.</span>
      </label>
    </div>
  )
}

/** A task step's by-when, in the review: this week, this month, a date, someday. */
export function TaskStepFields({ step, update }: { step: BreakdownStep; update: Update }) {
  const tk = step.task || {}
  const set = (patch: Partial<NonNullable<BreakdownStep['task']>>) => update('task', { ...tk, ...patch })
  const when = tk.when || ''
  return (
    <div className="space-y-2" data-testid="journey-task-fields">
      <div className="text-xs text-muted">A task: by when, roughly? Leave it open if you do not know yet.</div>
      <div className="flex flex-wrap gap-1.5" role="group" aria-label="By when">
        {WHEN_OPTIONS.map((o) => (
          <button key={o.value} type="button" aria-pressed={when === o.value} onClick={() => set({ when: when === o.value ? '' : o.value, due_date: o.value === 'date' ? tk.due_date : undefined })} className={chip(when === o.value)} data-testid={`journey-task-${o.value}`}>{o.label}</button>
        ))}
      </div>
      {when === 'date' && (
        <input type="date" value={tk.due_date || ''} onChange={(e) => set({ due_date: e.target.value })} aria-label="Due date"
               className="min-h-touch rounded-md border border-soft bg-surface px-2 text-sm text-primary lg:min-h-0 lg:py-1" data-testid="journey-task-date" />
      )}
    </div>
  )
}

/**
 * A skill step's target, in the review: how far this goal needs the skill to
 * come. It matters here rather than only on the skill page, because the skill's
 * own journey is planned to exactly this rung — and any step of this goal
 * waiting on the skill waits for it. Left as it is, the skill page asks later.
 */
export function SkillStepFields({ step, update }: { step: BreakdownStep; update: Update }) {
  const plan = (step.skill_plan || {}) as Record<string, unknown>
  const target = String(plan.target_level_for_goal || '')
  const set = (level: string) => update('skill_plan', { ...plan, target_level_for_goal: level || null })
  return (
    <div className="space-y-2" data-testid="journey-skill-fields">
      <div className="text-xs text-muted">A skill: how far does this goal need it to come? Its own journey gets planned to here.</div>
      <div className="flex flex-wrap gap-1.5" role="group" aria-label="How far this goal needs the skill">
        {LEVEL_OPTIONS.map((o) => (
          <button key={o.value} type="button" aria-pressed={target === o.value} onClick={() => set(target === o.value ? '' : o.value)} className={chip(target === o.value)} data-testid={`journey-skill-${o.value}`}>{o.label}</button>
        ))}
      </div>
      {plan.required_capability ? (
        <p className="text-xs text-secondary" data-testid="journey-skill-capability">What it lets you do: {String(plan.required_capability)}</p>
      ) : null}
    </div>
  )
}
